/**
 * Statuswerte, Rollen und der Feature-Workflow — die EINZIGE Quelle der Wahrheit.
 *
 * Server (Prisma-Enums, Validierung) und Web (Filter, Pills, Auswahlfelder) lesen
 * beide hier. Bewusst `as const`-Objekte statt TypeScript-`enum`: sie ueberleben
 * `isolatedModules`/`verbatimModuleSyntax` ohne Laufzeit-Sonderfall und lassen sich
 * direkt iterieren.
 *
 * Die deutschen Beschriftungen stehen hier mit dabei, damit Server-Fehlermeldungen
 * und UI-Labels nicht auseinanderlaufen. Die FARB-Zuordnung (Tints) trifft dagegen
 * die Komponente `StatusPill.vue` — Farbwerte gehören in die Design-Tokens, nicht
 * in die Vertragsschicht.
 */
// --- Benutzerrollen (Spec §5) ---------------------------------------------------
export const ROLES = ['admin', 'product_manager', 'developer'];
export const ROLE_LABELS = {
    admin: 'Administrator',
    product_manager: 'Produktmanager',
    developer: 'Entwickler'
};
export const USER_STATUSES = ['active', 'inactive'];
export const USER_STATUS_LABELS = {
    active: 'Aktiv',
    inactive: 'Deaktiviert'
};
// --- Produktstatus (Spec §6) ----------------------------------------------------
export const PRODUCT_STATUSES = ['active', 'development', 'maintenance', 'archived'];
export const PRODUCT_STATUS_LABELS = {
    active: 'Aktiv',
    development: 'Entwicklung',
    maintenance: 'Wartung',
    archived: 'Archiviert'
};
// --- Feature-Workflow (Spec §8) -------------------------------------------------
export const FEATURE_STATUSES = [
    'idea',
    'evaluation',
    'planned',
    'in_development',
    'testing',
    'released',
    'rejected'
];
export const FEATURE_STATUS_LABELS = {
    idea: 'Idee',
    evaluation: 'Bewertung',
    planned: 'Geplant',
    in_development: 'In Entwicklung',
    testing: 'Testing',
    released: 'Veröffentlicht',
    rejected: 'Verworfen'
};
/**
 * Erlaubte Statusuebergaenge. Der Workflow aus Spec §8 läuft vorwaerts; ein Schritt
 * zurück ist erlaubt (eine Fehleinschaetzung soll man korrigieren können, ohne das
 * Feature neu anzulegen), das Überspringen von Stufen dagegen nicht.
 *
 * `rejected` ist von jeder offenen Stufe erreichbar — ein Feature kann jederzeit
 * verworfen werden. Aus `released` führt kein Weg heraus: was veröffentlicht ist,
 * bleibt es; Nacharbeit ist ein neues Feature.
 */
export const FEATURE_STATUS_FLOW = {
    idea: ['evaluation', 'rejected'],
    evaluation: ['planned', 'idea', 'rejected'],
    planned: ['in_development', 'evaluation', 'rejected'],
    in_development: ['testing', 'planned', 'rejected'],
    testing: ['released', 'in_development', 'rejected'],
    released: [],
    rejected: ['idea']
};
/** Prüft, ob ein Statuswechsel laut Workflow erlaubt ist. Gleichbleiben ist immer ok. */
export function canTransition(from, to) {
    return from === to || FEATURE_STATUS_FLOW[from].includes(to);
}
/** Features in diesen Status gelten als offen (weder veröffentlicht noch verworfen). */
export const OPEN_FEATURE_STATUSES = [
    'idea',
    'evaluation',
    'planned',
    'in_development',
    'testing'
];
// --- Aktivität am Feature: Kommentare und Änderungsverlauf -----------------------
/**
 * Art eines Protokolleintrags. `fields` fasst die Stammdaten zusammen (Titel,
 * Beschreibung, Verantwortlicher, Zielversion), weil sie im selben Formular
 * geändert werden und im Strom als ein Vorgang erscheinen sollen. Status und
 * Bewertung haben eigene Arten, weil sie eigene Wege durchs System nehmen.
 */
export const FEATURE_CHANGE_KINDS = ['created', 'fields', 'status', 'evaluation'];
/**
 * Feldnamen im Protokoll und ihre Beschriftung. `owner` steht hier ohne `Id`:
 * Protokolliert wird der Name der Person, nicht ihre UUID — der Eintrag soll auch
 * dann noch lesbar sein, wenn es das Benutzerkonto nicht mehr gibt.
 */
export const FEATURE_FIELD_LABELS = {
    title: 'Titel',
    description: 'Beschreibung',
    owner: 'Verantwortlich',
    targetVersion: 'Zielversion',
    status: 'Status',
    priorityScore: 'Priorität'
};
// --- Benachrichtigungen (Spec §15) ----------------------------------------------
/**
 * Anlass eines Hinweises. Bewusst kurz gehalten: Was jemanden aus seiner Arbeit
 * reißt, muss es wert sein. Eine geänderte Bewertung steht deshalb nicht dabei —
 * eine Gewichtsanpassung bewegt alle Scores auf einmal und würde jeden Posteingang
 * fluten, ohne dass jemand etwas zu entscheiden hätte.
 */
export const NOTIFICATION_KINDS = ['comment', 'status', 'assigned', 'task'];
// --- Aufgaben an einem Feature --------------------------------------------------
/**
 * Art einer Aufgabe. `evaluate` ist der Anlass, um den es hier zuerst ging: „Bitte
 * bewerten" — und diese Aufgabe schließt sich von selbst, sobald die Bewertung steht.
 * `review` ist die allgemeine Bitte „schau dir das an / kümmere dich darum".
 */
export const TASK_KINDS = ['evaluate', 'review'];
export const TASK_KIND_LABELS = {
    evaluate: 'Bewerten',
    review: 'Prüfen'
};
export const TASK_STATUSES = ['open', 'done'];
export const TASK_STATUS_LABELS = {
    open: 'Offen',
    done: 'Erledigt'
};
// --- Roadmap (Spec §10) ---------------------------------------------------------
export const ROADMAP_ITEM_STATUSES = ['planned', 'in_progress', 'done', 'postponed'];
export const ROADMAP_ITEM_STATUS_LABELS = {
    planned: 'Geplant',
    in_progress: 'In Arbeit',
    done: 'Abgeschlossen',
    postponed: 'Verschoben'
};
//# sourceMappingURL=enums.js.map